import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { ApiProduct } from '@/api/types/menu'
import { useMenu } from '@/composables/useMenu'
import { useLocationStore } from '@/stores/location'
import { searchKey } from '@/utils/search'

/** Поиск включается с трёх символов: одна-две буквы совпадают со всем подряд. */
export const SEARCH_MIN_LENGTH = 2

/**
 * Меню целиком: выбор источника, поиск и производные структуры.
 * Перенос `store/menu.ts`.
 */
export const useMenuStore = defineStore('menu', () => {
  const locationStore = useLocationStore()

  const {
    folders,
    products,
    deliveryPrice,
    customMenu,
    loading,
    menuError,
    getRegionMenu,
    getCustomMenu,
  } = useMenu()

  const search = ref('')
  const activeFolderId = ref('')

  const currentFolderId = computed(() => activeFolderId.value || folders.value[0]?.id || '')

  /**
   * Ключи поиска по названиям: считаем один раз на меню, а не на каждое
   * нажатие — товаров сотни, и пересборка ключей на каждый символ заметна.
   */
  const searchKeys = computed(() => {
    const map = new Map<string, string>()
    for (const product of products.value) map.set(product.id, searchKey(product.name))
    return map
  })

  const currentProducts = computed<ApiProduct[]>(() => {
    if (search.value.trim().length <= SEARCH_MIN_LENGTH) return products.value

    /* Сравниваем ключи, а не сами строки: латиница должна находить кириллицу */
    const query = searchKey(search.value)
    if (!query) return products.value

    return products.value.filter((product) => searchKeys.value.get(product.id)?.includes(query))
  })

  /** Товар по id — корзина и избранное хранят только идентификаторы. */
  const productsEnum = computed<Record<string, ApiProduct>>(() =>
    products.value.reduce<Record<string, ApiProduct>>((acc, product) => {
      acc[product.id] = product
      return acc
    }, {}),
  )

  const productsByFolders = computed<Record<string, ApiProduct[]>>(() =>
    currentProducts.value.reduce<Record<string, ApiProduct[]>>((acc, product) => {
      const id = product.category?.id
      if (!id) return acc
      acc[id] = acc[id] ? [...(acc[id] ?? []), product] : [product]
      return acc
    }, {}),
  )

  /** Категории, в которых после поиска что-то осталось. */
  const visibleFolders = computed(() =>
    folders.value.filter((folder) => productsByFolders.value[folder.id]?.length),
  )

  /**
   * Источник меню (§5): при доставке решают координаты, при самовывозе —
   * выбранный ресторан. Нет ни того ни другого — витрина региона.
   */
  async function getMenu(): Promise<void> {
    const needCustom = locationStore.isDelivery
      ? locationStore.hasCoordinates
      : Boolean(locationStore.restaurantId)

    if (needCustom) await getCustomMenu()
    else await getRegionMenu()

    /*
     * Проверяем не id, а найденный филиал: id из cookie мог протухнуть
     * (сменили город, филиал выключили) — тогда его надо перезаписать тем,
     * что вернул бэкенд, иначе промокоды и слоты уедут на чужой ресторан.
     */
    if (!locationStore.activeRestaurant && customMenu.value?.restaurantId) {
      locationStore.setRestaurantId(customMenu.value.restaurantId)
    }
  }

  return {
    search,
    activeFolderId,
    folders,
    visibleFolders,
    products,
    productsEnum,
    productsByFolders,
    currentProducts,
    currentFolderId,
    deliveryPrice,
    customMenu,
    loading,
    menuError,
    getMenu,
    getRegionMenu,
  }
})
